import { type WatchProviders } from '@tmdb-graphql-api/schema/types';
import { isEmpty } from 'lodash-es';
import { ImageSize, LogoSize } from '#enums/imageSizes.ts';
import { type WatchProvider } from '#enums/watchProviders.ts';
import { buildImagePath } from '#helpers/buildImagePath.ts';
import { keys } from '#helpers/otherHelpers.ts';
import { type Config, type Country, type WatchProviderCountry } from '#types.ts';
import {
  type MovieWatchProvidersData,
  type RequiredWatchProviderFields,
  type TvWatchProvidersData,
  type WatchProviderCategoriesWithoutTypename,
} from '../types.ts';

const isWatchProviderCountry = (providers: WatchProviders, country: Country): country is WatchProviderCountry =>
  country in providers;

const format = (providers: WatchProviders, config: Config) => {
  let providerCategoryies: WatchProviderCategoriesWithoutTypename = {};

  if (isWatchProviderCountry(providers, config.country)) {
    providerCategoryies = providers[config.country] ?? {};
  }

  if (isEmpty(providerCategoryies)) {
    return [];
  }

  return keys(providerCategoryies).reduce((categories: [string, RequiredWatchProviderFields[]][], key) => {
    const providerCategory = providerCategoryies[key];

    if (!providerCategory) {
      return categories;
    }

    const providerFields = providerCategory.reduce((fields: RequiredWatchProviderFields[], provider) => {
      if (!provider) {
        return fields;
      }

      const { displayPriority, logoPath, providerName } = provider;

      if (!(logoPath && providerName)) {
        return fields;
      }

      fields.push({
        displayPriority: displayPriority ?? 0,
        logoPath: buildImagePath(logoPath, ImageSize.Logo, LogoSize.XXS, config.images),
        // External schema does not know the list of watch providers
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        providerName: providerName as WatchProvider,
      });

      return fields;
    }, []);

    if (providerFields.length === 0) {
      return categories;
    }

    providerFields.sort((a, b) => a.displayPriority - b.displayPriority);
    categories.push([key, providerFields]);
    return categories;
  }, []);
};

export const formatWatchProviders = (
  data: MovieWatchProvidersData | TvWatchProvidersData | null | undefined,
  config: Config,
) => {
  if (!data) {
    return [];
  }

  if ('movie' in data) {
    return data.movie.watchProviders ? format(data.movie.watchProviders, config) : [];
  }

  return data.tv.watchProviders ? format(data.tv.watchProviders, config) : [];
};
